import keyMirror from 'keymirror';
import shareColors from 'shared/constants/Colors';
import { fromJS } from 'immutable';

export const ValidCellFormatKeys = keyMirror({
  align: null,
  backColor: null,
  bold: null,
  fontSize: null,
  foreColor: null,
  italic: null,
});

export const ScreenSizes = {
  LargeScreen: 1200,
  MediumScreen: 992,
  SmallScreen: 768,
};

export const RowTypes = keyMirror({
  ItemSetTitle: null,
  TitleItem: null,
  Item: null,
  SubItem: null,
});

export const OverUnderStatuses = {
  OVER: 'over',
  UNDER: 'under',
};

export const EditableCellTypes = keyMirror({
  Data: null,
  ItemNumber: null,
  ItemTitle: null,
  ItemDescription: null,
  ItemDuration: null,
  ItemPresetDuration: null,
  ItemStartTime: null,
  Header: null,
  ItemNote: null,
  None: null,
});

export const DefaultShowColumnOrder = [
  EditableCellTypes.ItemNumber,
  EditableCellTypes.ItemTitle,
  EditableCellTypes.ItemDescription,
  EditableCellTypes.ItemStartTime,
  EditableCellTypes.ItemPresetDuration,
  EditableCellTypes.ItemDuration,
  EditableCellTypes.ItemNote,
];

export const DefaultGEColumnOrder = [
  EditableCellTypes.ItemNumber,
  'labels',
  EditableCellTypes.ItemTitle,
  EditableCellTypes.ItemDuration,
];

export const DefaultGridColumnOrder = [
  EditableCellTypes.ItemNumber,
  EditableCellTypes.ItemStartTime,
  EditableCellTypes.ItemPresetDuration,
  EditableCellTypes.ItemDuration,
  EditableCellTypes.ItemTitle,
  EditableCellTypes.ItemDescription,
  EditableCellTypes.ItemNote,
];

export const KeyScopes = keyMirror({
  CellEditing: null,
  Default: null,
  FullScreen: null,
  FullScreenEditing: null,
  None: null,
});

export const InlineStyleHotkeyBindings = [
  ['command+1, ctrl+1', '_handleCustomInlineStyleOneKeyPress'],
  ['command+2, ctrl+2', '_handleCustomInlineStyleTwoKeyPress'],
  ['command+3, ctrl+3', '_handleCustomInlineStyleThreeKeyPress'],
  ['command+4, ctrl+4', '_handleCustomInlineStyleFourKeyPress'],
  ['command+5, ctrl+5', '_handleCustomInlineStyleFiveKeyPress'],
  ['command+6, ctrl+6', '_handleCustomInlineStyleSixKeyPress'],
  ['command+7, ctrl+7', '_handleCustomInlineStyleSevenKeyPress'],
  ['command+8, ctrl+8', '_handleCustomInlineStyleEightKeyPress'],
  ['command+9, ctrl+9', '_handleCustomInlineStyleNineKeyPress'],
];

export const ItemColumnHeaderTitle = 'Item';
export const DescriptionColumnHeaderTitle = 'Description';
export const NoteColumnHeaderTitle = 'Private Notes';
export const ProofColumnHeaderTitle = 'Timestamp';
export const PlaceholderItemSegment = 'Item';
export const PlaceholderTitleRowItemSegment = 'Title Row';
export const PlaceholderItemDescription = 'Description';
export const DefaultRowHeight = 82;
export const DefaultRowHeightWithDescription = 82;
export const DefaultCompactRowHeight = 23;
export const DefaultGlobalElementRowHeight = 33;
export const DefaultCompactTitleRowHeight = 53;
export const DefaultTitleRowHeight = 80;
export const HeaderHeight = 50;
export const CompactHeaderHeight = 35;
export const DefaultEditableCellTextAreaHeight = 16;
export const ReadOnlyCellAddonHeightWithPersonalHighlight = 4;

/**
 * Preset widths of mobile table Item column
 */
export const MobileItemColumnWidths = {
  UltraNarrow: 142,
  Narrow: 284,
  Normal: 430,
  Wide: 565,
  ScriptWide: 805,
};
/**
 * Preset widths of mobile table Note column
 */

export const MobileNoteColumnWidths = {
  UltraNarrow: 85,
  Narrow: 125,
  Normal: 250,
  Wide: 375,
  ScriptWide: 520,
};

/**
 * Preset widths of Item column
 */

export const ItemColumnWidths = {
  UltraNarrow: 190,
  Narrow: 270,
  Normal: 375,
  Wide: 575,
  ScriptWide: 819,
};

/**
 * Preset widths of segment fields (inside Item column)
 */
export const ItemColumnSegmentWidths = {
  UltraNarrow: 145,
  Narrow: 210,
  Normal: 313,
  Wide: 515,
  ScriptWide: 760,
};

/**
 * Preset widths of data columns
 */
export const ColumnWidths = {
  UltraNarrow: 85,
  Narrow: 125,
  Normal: 250,
  Wide: 375,
  ScriptWide: 520,
};

/**
 * Preset widths of data columns
 */
export const CompactSpecialColumnWidths = {
  RowMenuBar: 90,
  Start: 110,
  Preset: 110,
  Duration: 120,
  Proof: 238,
  Featured: 32,
};

export const ColumnWidthTokens = [
  'ultraNarrow',
  'narrow',
  'normal',
  'wide',
  'scriptWide',
];

export const ColumnWidthStrings = {
  UltraNarrow: 'ultraNarrow',
  Narrow: 'narrow',
  Normal: 'normal',
  Wide: 'wide',
  ScriptWide: 'scriptWide',
};

export const ColumnWidthClassNames = {
  UltraNarrow: 'ultra-narrow',
  Narrow: 'narrow',
  Normal: 'normal',
  Wide: 'wide',
  ScriptWide: 'script-wide',
};

export const PasteModes = keyMirror({
  Cut: null,
  Paste: null,
});

export const ResponsiveDisplayModes = keyMirror({
  Table: null,
  Shotgun: null,
});

export const validImageTypes = ['bmp', 'gif', 'jpeg', 'jpg', 'png'];

export const FontSizeMap = {
  small: 10,
  normal: 14,
  large: 18,
  xlarge: 30,
};

export const CellBorders = {
  Compact: 2,
  Loose: 9,
  CompactHeader: 0,
  LooseHeader: 0,
  GridHeader: 4,
};

export const CellPaddings = {
  Compact: 8,
  Loose: 20,
  CompactTitle: 24,
  Grid: 10,
  GridHeader: 10,
  CompactHeader: 5,
  LooseHeader: 10,
};

export const FontSizesList = ['small', 'normal', 'large', 'xlarge'];

export const RTEFontSizeArray = [
  8, 9, 10, 11, 12, 14, 18, 24, 30, 36, 48, 60, 72,
];

export const Colors = shareColors;

export const ColorRGBs = {
  pink0: 'rgb(255,0,255)',
  green0: 'rgb(0,234,112)',
  yellow0: 'rgb(255,228,0)',
  orange0: 'rgb(249,105,14)',
  brown0: 'rgb(97,65,38)',
  red0: 'rgb(242,39,19)',
  black0: 'rgb(0,0,0)',
  blue0: 'rgb(65,131,215)',
  purple0: 'rgb(154,18,179)',
  pink1: 'rgb(253,247,253)',
  green1: 'rgb(249,253,249)',
  yellow1: 'rgb(255,254,240)',
  orange1: 'rgb(255,249,245)',
  brown1: 'rgb(250,248,246)',
  red1: 'rgb(255,245,244)',
  black1: 'rgb(255,255,255)',
  blue1: 'rgb(243,247,253)',
  purple1: 'rgb(250,243,251)',
  pink2: 'rgb(249,228,249)',
  green2: 'rgb(235,249,235)',
  yellow2: 'rgb(255,251,204)',
  orange2: 'rgb(255,236,220)',
  brown2: 'rgb(239,231,225)',
  red2: 'rgb(254,222,216)',
  black2: 'rgb(242,243,244)',
  blue2: 'rgb(189,212,241)',
  purple2: 'rgb(220,172,228)',
  pink3: 'rgb(239,179,238)',
  green3: 'rgb(199,238,199)',
  yellow3: 'rgb(255,243,109)',
  orange3: 'rgb(254,201,156)',
  brown3: 'rgb(208,187,169)',
  red3: 'rgb(253,161,152)',
  black3: 'rgb(217,221,223)',
  blue3: 'rgb(141,181,231)',
  purple3: 'rgb(204,136,217)',
  pink4: 'rgb(247,90,246)',
  green4: 'rgb(99,236,156)',
  yellow4: 'rgb(255,237,65)',
  orange4: 'rgb(251,153,85)',
  brown4: 'rgb(152,126,104)',
  red4: 'rgb(253,161,152)',
  black4: 'rgb(141,144,145)',
  blue4: 'rgb(103,156,233)',
  purple4: 'rgb(179,77,198)',
  pink5: 'rgb(209,0,184)',
  green5: 'rgb(0,169,80)',
  yellow5: 'rgb(223,208,0)',
  orange5: 'rgb(204,79,0)',
  brown5: 'rgb(66,44,25)',
  red5: 'rgb(181,15,0)',
  black5: 'rgb(66,67,67)',
  blue5: 'rgb(0,64,146)',
  purple5: 'rgb(110,12,129)',
  inherit: 'inherit',
  white: 'rgb(255,255,255)',
  black: 'rgb(0,0,0)',
  pink: 'rgb(158,80,247)',
  red: 'rgb(243,110,113)',
  green: 'rgb(82,255,86)',
  yellow: 'rgb(247,254,89)',
  teal: 'rgb(93,208,217)',
  brown: 'rgb(244,184,75)',
  grey: 'rgb(222,222,222)',
  lightgreen: 'rgb(198,255,199)',
  transparent: 'rgb(0,0,0, 0)',
};

function createRteState(text) {
  return {
    blocks: [
      {
        data: {},
        entityRanges: [],
        inlineStyleRanges: [],
        depth: 0,
        type: 'unstyled',
        text,
        key: '9rinj',
      },
    ],
    entityMap: {
      0: {
        type: 'PLACEHOLDER',
      },
    },
  };
}

const emptyState = createRteState('');

const corruptedDataText =
  'Row did not render. Contact Shoflo Support. This row is no longer editable';

export const PlaceholderItem = fromJS({
  cells: {
    '222222222222222222222222': {
      rteState: emptyState,
      text: '',
    },
  },
  createdOn: 0,
  deleted: false,
  description: '',
  displayDurationClock: false,
  displayPresetClock: false,
  displayStartTimeClock: false,
  durationInSeconds: 0,
  durationSuppressed: true,
  locked: false,
  modifiedOn: 0,
  number: '0',
  rteDescriptionState: emptyState,
  rteTitleState: createRteState(corruptedDataText),
  runTimeClockSuppressed: true,
  settings: {},
  startOn: 0,
  title: corruptedDataText,
  titleView: true,
  userSettings: {},
});

export const DefaultColumnWidth = ColumnWidths.Narrow;
export const DefaultItemColumnWidth = ItemColumnWidths.Normal;
export const DefaultCompactItemColumnWidth = ItemColumnSegmentWidths.Narrow;
export const DefaultItemColumnSegmentWidth = ItemColumnSegmentWidths.Narrow;
